import React, { useState } from "react";
import { Link, NavLink, Navigate, useNavigate, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  Wallet,
  Ticket,
  ScanLine,
  ScrollText,
  ShieldCheck,
  Users,
  ClipboardCheck,
  HandCoins,
  ListChecks,
  FileSearch,
  Menu,
  X,
  LogOut,
  TestTube2,
  MessageCircle,
  MessageSquare,
} from "lucide-react";
import { LogoMark } from "./Logo";
import { useAuth } from "@/lib/auth";

const MERCHANT_LINKS_ALL = [
  { to: "/dashboard", label: "Overview", icon: LayoutDashboard, always: true },
  { to: "/dashboard/kyc", label: "KYC", icon: ShieldCheck, always: true },
  { to: "/dashboard/wallet", label: "Wallet", icon: Wallet, kycOnly: true },
  { to: "/dashboard/vouchers", label: "Vouchers", icon: Ticket, kycOnly: true },
  { to: "/dashboard/redeem", label: "Redeem", icon: ScanLine, kycOnly: true },
  { to: "/dashboard/history", label: "History", icon: ScrollText, always: true },
];

const ADMIN_LINKS_BASE = [
  { to: "/admin", label: "Overview", icon: LayoutDashboard },
  { to: "/admin/merchants", label: "Merchants", icon: Users },
  { to: "/admin/kyc", label: "KYC Queue", icon: ClipboardCheck },
  { to: "/admin/wallet", label: "Top-ups", icon: HandCoins },
  { to: "/admin/vouchers", label: "Voucher Liabilities", icon: Ticket },
  { to: "/admin/audit", label: "Audit Log", icon: FileSearch },
  { to: "/admin/sms", label: "SMS Log", icon: ListChecks },
];
const ADMIN_QA_LINK = { to: "/qa", label: "QA Console", icon: TestTube2 };

export function ProtectedRoute({ children, requireRole }) {
  const { user } = useAuth();
  const loc = useLocation();
  if (user === undefined)
    return (
      <div className="min-h-screen flex items-center justify-center text-[#0A2540]" data-testid="auth-loading">
        Loading…
      </div>
    );
  if (!user) return <Navigate to={`/login?next=${encodeURIComponent(loc.pathname)}`} replace />;
  if (requireRole && user.role !== requireRole) return <Navigate to="/" replace />;
  return children;
}

export function DashboardShell({ children, role = "merchant" }) {
  const [open, setOpen] = useState(false);
  const { user, wallet, merchant, env, logout } = useAuth();
  const navigate = useNavigate();
  const kycApproved = merchant?.kyc_status === "approved";
  const merchantLinks = MERCHANT_LINKS_ALL.filter((l) => l.always || (l.kycOnly && kycApproved));
  const adminLinks = env?.enable_dev_otp ? [...ADMIN_LINKS_BASE, ADMIN_QA_LINK] : ADMIN_LINKS_BASE;
  const links = role === "admin" ? adminLinks : merchantLinks;

  return (
    <div className="min-h-screen bg-[#F8F9FA] flex flex-col">
      {/* Top bar (sticky) */}
      <header className="sticky top-0 z-40 bg-white border-b border-[#E5E7EB]">
        <div className="px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              data-testid="dashboard-menu-toggle"
              onClick={() => setOpen((v) => !v)}
              className="lg:hidden inline-flex items-center justify-center w-10 h-10 rounded-md border border-[#E5E7EB] text-[#0A2540]"
              aria-label="Toggle sidebar"
            >
              {open ? <X size={20} /> : <Menu size={20} />}
            </button>
            <Link to="/" data-testid="dashboard-logo-link"><LogoMark /></Link>
          </div>
          <div className="flex items-center gap-3">
            {role === "merchant" && wallet && (
              <div className="hidden sm:block text-right">
                <p className="text-xs uppercase tracking-[0.18em] text-[#00C4B4] font-bold">Wallet</p>
                <p data-testid="header-wallet-balance" className="font-mono font-semibold text-[#0A2540]">
                  R {Number(wallet.balance || 0).toFixed(2)}
                </p>
              </div>
            )}
            <div className="text-right hidden sm:block">
              <p className="text-sm font-semibold text-[#0A2540]" data-testid="header-user-name">{user?.full_name}</p>
              <p className="text-xs text-[#666666] uppercase tracking-wider">{user?.role}</p>
            </div>
            <button
              data-testid="dashboard-logout-btn"
              onClick={async () => {
                await logout();
                navigate("/");
              }}
              className="inline-flex items-center gap-2 text-sm font-medium px-3 py-2 rounded-md text-[#0A2540] hover:bg-[#F8F9FA]"
            >
              <LogOut size={16} /> <span className="hidden sm:inline">Sign out</span>
            </button>
          </div>
        </div>
      </header>

      <div className="flex flex-1">
        {/* Sidebar */}
        <aside
          className={`${
            open ? "translate-x-0" : "-translate-x-full"
          } lg:translate-x-0 fixed lg:sticky lg:top-16 top-16 left-0 z-30 w-64 h-[calc(100vh-4rem)] bg-white border-r border-[#E5E7EB] transition-transform`}
        >
          <nav className="p-4 space-y-1">
            {links.map((l) => {
              const Icon = l.icon;
              return (
                <NavLink
                  key={l.to}
                  to={l.to}
                  end={l.to === "/dashboard" || l.to === "/admin"}
                  data-testid={`sidebar-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                  onClick={() => setOpen(false)}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
                      isActive
                        ? "bg-[#0A2540] text-white"
                        : "text-[#333333] hover:bg-[#F8F9FA] hover:text-[#0A2540]"
                    }`
                  }
                >
                  <Icon size={18} />
                  {l.label}
                </NavLink>
              );
            })}
          </nav>
          <div className="p-4 mx-4 mt-2 rounded-lg bg-[#F8F9FA] border border-[#E5E7EB]">
            <p className="text-xs uppercase tracking-[0.18em] text-[#00C4B4] font-bold">Need help?</p>
            <p className="mt-2 text-xs text-[#666666] leading-relaxed">
              info@praxio.co.za<br />079 485 2928
            </p>
            <div className="mt-3 grid grid-cols-2 gap-2 lg:hidden" data-testid="help-mobile-cta">
              <a
                href="https://wa.me/27794852928?text=Hi%20FlexiVouch%20support"
                target="_blank"
                rel="noreferrer"
                data-testid="help-whatsapp-btn"
                className="inline-flex items-center justify-center gap-1.5 text-xs font-semibold py-2 rounded-md bg-[#25D366] text-white hover:bg-[#1ebd5a] transition-colors"
              >
                <MessageCircle size={14} /> WhatsApp
              </a>
              <a
                href="sms:+27794852928?body=Hi%20FlexiVouch%20support"
                data-testid="help-sms-btn"
                className="inline-flex items-center justify-center gap-1.5 text-xs font-semibold py-2 rounded-md bg-[#0A2540] text-white hover:bg-[#133D6B] transition-colors"
              >
                <MessageSquare size={14} /> SMS
              </a>
            </div>
          </div>
        </aside>

        {open && (
          <div className="lg:hidden fixed inset-0 z-20 bg-black/30" onClick={() => setOpen(false)} />
        )}

        <main className="flex-1 px-4 sm:px-6 lg:px-10 py-8 max-w-7xl mx-auto w-full">{children}</main>
      </div>
    </div>
  );
}
